import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle } from "lucide-react";
import { useInView } from "@/hooks";
import { contactMessageService } from "@/services/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import toast from "react-hot-toast";

export function ContactSection() {
  const { ref, isInView } = useInView();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    type: "client" as const,
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await contactMessageService.create(form);
      setSent(true);
      toast.success("Message envoyé avec succès !");
      setForm({ name: "", phone: "", email: "", type: "client", subject: "", message: "" });
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-20 bg-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Nous sommes à votre écoute.
          </h2>
          <p className="text-lg text-white/60">
            Une question, un partenariat, une idée ? Contactez l'équipe Allo Artisan.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            {sent ? (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-10 text-center border border-white/10">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Message envoyé !</h3>
                <p className="text-white/60">Nous vous répondrons dans les plus brefs délais.</p>
                <Button
                  variant="outline"
                  className="mt-6 border-white/20 text-white hover:bg-white/10"
                  onClick={() => setSent(false)}
                >
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">

                  <div className="grid gap-1.5">
                    <label htmlFor="name" className="text-white text-xs">Nom complet</label> 
                    <Input
                      id="name"
                      placeholder="Votre nom"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className="bg-white/10 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <label htmlFor="phone" className="text-white text-xs">Téléphone</label>
                    <Input
                      id="phone"
                      placeholder="+225 XX XX XX XX"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required
                      className="bg-white/10 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>
                
                <div className="grid gap-1.5">
                  <label htmlFor="email" className="text-white text-xs">Email (optionnel)</label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-white/10 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                
                <div className="grid gap-1.5">
                  <label htmlFor="type" className="text-white text-xs">Objet</label>
                  <Select
                    id="type"
                    options={[
                      { value: "client", label: "Client" },
                      { value: "artisan", label: "Artisan" },
                      { value: "partnership", label: "Partenariat" },
                      { value: "investor", label: "Investisseur" },
                      { value: "other", label: "Autre" },
                    ]}
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as typeof form.type })}
                    className="bg-white/10 border-white/10 text-white [&_option]:bg-neutral-900 [&_option]:text-white"
                  />
                </div>
                
                <div className="grid gap-1.5">
                  <label htmlFor="message" className="text-white text-xs">Votre message</label>
                  <Textarea
                    id="message"
                    placeholder="Décrivez votre demande..."
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    className="bg-white/10 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>
                
                <Button
                  type="submit"
                  size="lg"
                  rounded="xl"
                  isLoading={loading}
                  className="w-full"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer le message
                </Button>
              </form>
            )}
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Téléphone</h4>
                  <p className="text-white/60 text-sm">+225 01 40 65 10 65</p>
                  <p className="text-white/40 text-xs mt-1">Monsieur Boga — Co-fondateur</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">WhatsApp</h4>
                  <p className="text-white/60 text-sm">+225 01 42 20 46 01</p>
                  <p className="text-white/40 text-xs mt-1">Monsieur Boga — Co-fondateur</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-secondary" />
                </div>
                <div>
                    <h4 className="text-white font-semibold mb-1">Email</h4>
                    <p className="text-white/60 text-sm">alloartisan20@gmail.com</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Localisation</h4>
                  <p className="text-white/60 text-sm">Abidjan, Côte d'Ivoire</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
